import React, { Dispatch, SetStateAction } from 'react';
import { Alert, Button } from '@printdeal/ui-library';
import { FormattedMessage } from 'react-intl';
import { AddressError, ShippingAddress, useValidateAddress } from '@printdeal/checkout-frontend';
import useSetShippingAddressMutation, { SetShippingAddressResponse } from './queries/useSetShippingAddressMutation';

interface Props {
  lineItemIds: string[];
  address: ShippingAddress;
  setShippingAddressMutation: ReturnType<typeof useSetShippingAddressMutation>;
  setView: Dispatch<SetStateAction<'list' | 'create' | 'edit'>>;
  mutatingAddressId: string | undefined;
  setMutatingAddressId: Dispatch<SetStateAction<string | undefined>>;
  setDeleteAddressModalIsOpen: Dispatch<SetStateAction<string | undefined>>;
  onChangeSuccess: (data: SetShippingAddressResponse) => void;
  onChangeError: () => void;
}

const ShippingAddressPanelFooter = ({
  lineItemIds,
  address,
  setShippingAddressMutation,
  setView,
  mutatingAddressId,
  setMutatingAddressId,
  setDeleteAddressModalIsOpen,
  onChangeSuccess,
  onChangeError,
}: Props) => {
  const validationErrors = useValidateAddress({
    address,
    requireEmail: false,
  });

  if (validationErrors.length > 0) {
    return (
      <Alert variant="error">
        <div className="pd-flex pd-flex-col pd-gap-2">
          <FormattedMessage
            id="checkout.invalidAddress"
            defaultMessage="This address can currently not be selected, because it is missing certain data or contains invalid data. To make the address usable again, you can edit the address and fix the validation errors. Alternatively, if you no longer need this address, you can delete it."
          />

          <ul>
            {validationErrors.map((error) => (
              <li key={error.path}>
                <AddressError error={error} />
              </li>
            ))}
          </ul>

          <div className="pd-flex pd-gap-2">
            <Button
              variant="secondary"
              iconBefore="edit"
              onClick={() => {
                setMutatingAddressId(address.id);
                setView('edit');
              }}
            >
              <FormattedMessage id="checkout.editAddress" defaultMessage="Edit address" />
            </Button>

            <Button
              variant="secondary"
              iconBefore="trashcan"
              onClick={() => setDeleteAddressModalIsOpen(address.id)}
            >
              <FormattedMessage id="checkout.deleteAddress" defaultMessage="Delete address" />
            </Button>
          </div>
        </div>
      </Alert>
    );
  }

  return (
    <Button
      className="pd-w-full md:pd-w-auto"
      isLoading={setShippingAddressMutation.isPending && mutatingAddressId === address.id}
      onClick={() => {
        setMutatingAddressId(address.id);
        setShippingAddressMutation.mutateAsync({
          lineItems: lineItemIds,
          address: {
            id: address.id.trim(),
            firstName: address.firstName.trim(),
            lastName: address.lastName.trim(),
            ...address.email && { email: address.email.trim() },
            ...address.phone && { phone: address.phone.trim() },
            ...address.company && { company: address.company.trim() },
            country: address.country.trim(),
            postalCode: address.postalCode.trim(),
            streetNumber: address.streetNumber.trim(),
            streetName: address.streetName.trim(),
            city: address.city.trim(),
            ...address.additionalStreetInfo && { additionalStreetInfo: address.additionalStreetInfo.trim() },
          },
        }).then((data) => {
          setShippingAddressMutation.reset();
          onChangeSuccess(data);
        }).catch(() => {
          onChangeError();
        });
      }}
    >
      <FormattedMessage id="checkout.button.selectAddress" defaultMessage="Select address" />
    </Button>
  );
};

export default ShippingAddressPanelFooter;
